import ApiClient from '@/api/ApiClient';
import BaseService from './BaseService';
import SupportTicket from '@/api/models/SupportTicket';
import SupportReason from '@/api/models/SupportReason';
import SupportTicketReply from '@/api/models/SupportTicketReply';

class SupportService extends BaseService {
    #prefixUrl = 'api/support/';

    constructor() {
        super();
        this.api = new ApiClient(this.#prefixUrl);
    }

    /**
     * Get all SupportTickets (admin: all, user: own)
     * @param {Object} filters - { status }
     * @returns {Array<SupportTicket>}
     */
    async get(filters = {}) {
        const supportTickets = [];
        const response = await this.api.executeRequest(`/`, filters, this.api.methods.GET);
        if (response.success() && response.getContent() != null) {
            for (let data of response.getContent()) {
                supportTickets.push(new SupportTicket(data));
            }
        }
        return supportTickets;
    }

    /**
     * Get a single SupportTicket by uuid (includes replies)
     * @param {String} uuid
     * @returns {SupportTicket|null}
     */
    async find(uuid) {
        const response = await this.api.executeRequest(`/${uuid}`, {}, this.api.methods.GET);
        if (response.success() && response.getContent() != null) {
            return new SupportTicket(response.getContent());
        }
        return null;
    }

    /**
     * Create a new SupportTicket
     * @param {Object} data - { name, course_uuid, reason_id, message }
     * @returns {Response}
     */
    async store(data = {}) {
        return await this.api.executeRequest(`/`, data, this.api.methods.POST);
    }

    /**
     * Close a SupportTicket
     * @param {String} uuid
     * @returns {Response}
     */
    async close(uuid) {
        return await this.api.executeRequest(`/${uuid}/close`, {}, this.api.methods.PUT);
    }

    /**
     * Assign ticket to logged admin
     * @param {String} uuid
     * @returns {Response}
     */
    async assign(uuid) {
        return await this.api.executeRequest(`/${uuid}/assign`, {}, this.api.methods.PUT);
    }

    /**
     * Unassign ticket
     * @param {String} uuid
     * @returns {Response}
     */
    async unassign(uuid) {
        return await this.api.executeRequest(`/${uuid}/unassign`, {}, this.api.methods.PUT);
    }

    /**
     * Update ticket status (admin only)
     * @param {String} uuid
     * @param {String} status
     * @returns {Response}
     */
    async updateStatus(uuid, status) {
        return await this.api.executeRequest(`/${uuid}/status`, { status }, this.api.methods.PUT);
    }

    /**
     * Get all active SupportReasons
     * @returns {Array<SupportReason>}
     */
    async getReasons() {
        const reasons = [];
        const response = await this.api.executeRequest(`/reasons`, {}, this.api.methods.GET);
        if (response.success() && response.getContent() != null) {
            for (let data of response.getContent()) {
                reasons.push(new SupportReason(data));
            }
        }
        return reasons;
    }

    /**
     * Busca as respostas de um chamado
     * @param {String} uuid
     * @returns {Array}
     */
    async getReplies(uuid) {
        const reasons = [];
        const response = await this.api.executeRequest(`/replies/${uuid}`, {}, this.api.methods.GET);
        if (response.success() && response.getContent() != null) {
            for (let data of response.getContent()) {
                reasons.push(new SupportTicketReply(data));
            }
        }
        return reasons;
    }
    
    /**
     * Envia uma resposta a um chamado
     * @param {String} uuid
     * @param {Object} payload - { message }
     * @returns {Response}
     */
    async reply(uuid, payload) {
        return await this.api.executeRequest(`/reply/${uuid}`, payload, this.api.methods.POST);
    }

    /**
     * Checks if there is new Replies
     * @param {Integer} supportTicketId
     * @returns {Boolean}
     */
    async checkNewReplies(supportTicketUuid = null, supportTicketRepliesIds = []) {
        const response = await this.api.executeRequest(`/has_new_replies/${supportTicketUuid}`, {
            ids: supportTicketRepliesIds
        }, this.api.methods.POST);

        return response.success();
    }
}

export default new SupportService();